import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { insiderApi } from '../../services/api';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Icon sx={{ fontSize: 40, color, mr: 2 }} />
        <Box>
          <Typography variant="h4" sx={{ color }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function Dashboard() {
  const [account, setAccount] = useState(null);
  const [stats, setStats] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [positions, setPositions] = useState([]);
  const [recentTrades, setRecentTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [accountRes, statsRes, perfRes, posRes, tradesRes] = await Promise.all([
        insiderApi.getAccount(),
        insiderApi.getStats(),
        insiderApi.getPerformance(),
        insiderApi.getPositions(),
        insiderApi.getTrades({ page: 1, per_page: 10 }),
      ]);
      setAccount(accountRes.data);
      setStats(statsRes.data);
      setPerformance(perfRes.data || []);
      setPositions(posRes.data || []);
      setRecentTrades(tradesRes.data.trades || []);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalPL = account && account.portfolio_value != null ? (account.portfolio_value - 100000) : 0;
  const totalPLPct = (totalPL / 100000) * 100;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Insider Trading Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Portfolio Value"
            value={account ? `$${account.portfolio_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
            subtitle={`${totalPLPct >= 0 ? '+' : ''}${totalPLPct.toFixed(2)}%`}
            icon={AccountBalanceWalletIcon}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Trades"
            value={stats ? stats.total_trades : 0}
            subtitle={`${stats ? stats.open_trades : 0} open, ${stats ? stats.closed_trades : 0} closed`}
            icon={ShowChartIcon}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Win Rate"
            value={stats && stats.win_rate != null ? `${stats.win_rate.toFixed(1)}%` : '0%'}
            subtitle={`${stats ? stats.winning_trades || 0 : 0} wins, ${stats ? stats.losing_trades || 0 : 0} losses`}
            icon={TrendingUpIcon}
            color={stats && stats.win_rate != null && stats.win_rate >= 50 ? '#2e7d32' : '#d32f2f'}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Realized P/L"
            value={stats && stats.total_realized_pl != null ? `$${stats.total_realized_pl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
            subtitle={stats && stats.avg_return_pct != null ? `Avg: ${(stats.avg_return_pct * 100).toFixed(2)}%` : 'Avg: 0%'}
            icon={stats && stats.total_realized_pl != null && stats.total_realized_pl >= 0 ? TrendingUpIcon : TrendingDownIcon}
            color={stats && stats.total_realized_pl != null && stats.total_realized_pl >= 0 ? '#2e7d32' : '#d32f2f'}
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Portfolio Performance (30 Days)
            </Typography>
            {performance.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US')}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="portfolio_value"
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.3}
                    name="Portfolio Value"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                No performance data available yet
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              P/L Over Time
            </Typography>
            {performance.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={performance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US')}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total_pl"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    name="Total P/L"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                No P/L data available yet
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Account Breakdown
            </Typography>
            {account && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Cash:</Typography>
                  <Typography>${(account.cash ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Stock Holdings:</Typography>
                  <Typography>${(account.long_market_value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Buying Power:</Typography>
                  <Typography fontWeight="bold">${(account.buying_power ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography fontWeight="bold">Total P/L:</Typography>
                  <Typography
                    fontWeight="bold"
                    sx={{ color: totalPL >= 0 ? '#2e7d32' : '#d32f2f' }}
                  >
                    {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({totalPLPct >= 0 ? '+' : ''}{totalPLPct.toFixed(2)}%)
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Open Positions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Open Positions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Avg Entry</TableCell>
                    <TableCell align="right">Current Price</TableCell>
                    <TableCell align="right">Market Value</TableCell>
                    <TableCell align="right">Unrealized P/L</TableCell>
                    <TableCell align="right">P/L %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" py={2}>
                          No open positions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    positions.map((pos) => {
                      const avgEntry = pos.avg_entry_price || 0;
                      const currentPrice = pos.current_price || 0;
                      const qty = pos.qty || 0;
                      const marketValue = pos.market_value || 0;
                      const plPct = avgEntry !== 0 ? ((currentPrice - avgEntry) / avgEntry) * 100 : 0;
                      const unrealizedPL = (currentPrice - avgEntry) * qty;
                      return (
                        <TableRow key={pos.symbol}>
                          <TableCell>
                            <Typography fontWeight="bold">{pos.symbol}</Typography>
                          </TableCell>
                          <TableCell align="right">{qty}</TableCell>
                          <TableCell align="right">${avgEntry.toFixed(2)}</TableCell>
                          <TableCell align="right">${currentPrice.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            ${marketValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ color: unrealizedPL >= 0 ? '#2e7d32' : '#d32f2f' }}>
                              {unrealizedPL >= 0 ? '+' : ''}${unrealizedPL.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${plPct >= 0 ? '+' : ''}${plPct.toFixed(2)}%`}
                              size="small"
                              sx={{
                                bgcolor: plPct >= 0 ? '#e8f5e9' : '#ffebee',
                                color: plPct >= 0 ? '#2e7d32' : '#d32f2f',
                                fontWeight: 'bold',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Trade History */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Trades
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Side</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Entry</TableCell>
                    <TableCell align="right">Exit</TableCell>
                    <TableCell align="right">P/L</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTrades.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary" py={2}>
                          No trades yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentTrades.map((trade) => {
                      const isClosed = trade.status === 'closed';
                      const pl = isClosed ? (trade.realized_pl || 0) : (trade.unrealized_pl || 0);
                      return (
                        <TableRow key={trade.trade_id}>
                          <TableCell>{formatDate(trade.entry_time)}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">{trade.symbol}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={trade.side}
                              size="small"
                              sx={{
                                bgcolor: trade.side === 'buy' ? '#e3f2fd' : '#ffebee',
                                color: trade.side === 'buy' ? '#1565c0' : '#c62828',
                                fontWeight: 'bold',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{trade.quantity}</TableCell>
                          <TableCell align="right">${trade.entry_price?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell align="right">
                            {isClosed && trade.exit_price != null ? `$${trade.exit_price.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ color: pl >= 0 ? '#2e7d32' : '#d32f2f' }}>
                              {pl >= 0 ? '+' : ''}${pl?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={trade.status}
                              size="small"
                              color={trade.status === 'open' ? 'primary' : 'default'}
                              variant={trade.status === 'open' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
